import React, { useCallback, useMemo, useState } from "react";
import DynamicTable from "../../../../../unicomponents/Tables/DynamicTable";
import styles from "../../css/ParentEducation.module.css";

const ATTENDANCE_OPTIONS = ["", "부", "모", "부모", "보호자", "출석인정", "불참"];
const isIntegratedPeriod = (hierarchyOne) =>
  String(hierarchyOne ?? "") === "" || String(hierarchyOne ?? "") === "0";

const scopeMatch = (period, schoolYearId, termId, hierarchyOneId) => {
  if (String(period.school_year_id) !== String(schoolYearId)) return false;
  if (String(period.term_id) !== String(termId)) return false;
  const targetCourse = String(hierarchyOneId);
  const periodCourse = String(period.hierarchy_one ?? "");
  return periodCourse === "" || periodCourse === "0" || periodCourse === targetCourse;
};

export default function ParentEducationListPage({
  schoolId,
  filters,
  periods,
  students,
  attendances,
  settingData,
  termPeriodData,
  canAccess,
  handleToastCenterTop,
  upsertAttendanceBulkMutation,
}) {
  const [staged, setStaged] = useState({});

  const groupNameById = useMemo(() => {
    const map = {};
    (settingData || []).forEach((row) => {
      map[String(row.id)] = row.group_name;
    });
    return map;
  }, [settingData]);

  const termNameById = useMemo(() => {
    const map = {};
    (termPeriodData || []).forEach((row) => {
      map[String(row.id)] = `${row.term_name}${row.term_category || ""}`;
    });
    return map;
  }, [termPeriodData]);

  const periodColumns = useMemo(() => {
    if (!filters.schoolYearId || !filters.termId || !filters.hierarchyOneId) return [];
    return (periods || [])
      .filter((row) =>
        scopeMatch(row, filters.schoolYearId, filters.termId, filters.hierarchyOneId),
      )
      .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0));
  }, [periods, filters.schoolYearId, filters.termId, filters.hierarchyOneId]);

  const attendanceMap = useMemo(() => {
    const map = {};
    (attendances || []).forEach((row) => {
      map[`${row.period_id}::${row.username}`] = row.value || "";
    });
    return map;
  }, [attendances]);

  const filteredStudents = useMemo(() => {
    return (students || []).filter((row) => {
      if (String(row.school_year_id) !== String(filters.schoolYearId)) return false;
      if (String(row.hierarchy_one) !== String(filters.hierarchyOneId)) return false;
      if (
        filters.hierarchyTwoId &&
        filters.hierarchyTwoId !== "0" &&
        String(row.hierarchy_two) !== String(filters.hierarchyTwoId)
      ) {
        return false;
      }
      if (
        filters.hierarchyThreeId &&
        filters.hierarchyThreeId !== "0" &&
        String(row.hierarchy_three) !== String(filters.hierarchyThreeId)
      ) {
        return false;
      }
      return true;
    });
  }, [
    students,
    filters.schoolYearId,
    filters.hierarchyOneId,
    filters.hierarchyTwoId,
    filters.hierarchyThreeId,
  ]);

  const resolveCellValue = useCallback(
    (periodId, username) => {
      const key = `${periodId}::${username}`;
      if (Object.prototype.hasOwnProperty.call(staged, key)) return staged[key];
      return attendanceMap[key] || "";
    },
    [staged, attendanceMap],
  );

  const tableRows = useMemo(() => {
    return filteredStudents.map((student) => {
      const row = {
        id: student.username,
        hierarchyOne: groupNameById[String(student.hierarchy_one)] || "",
        fullname: student.fullname,
        hierarchyTwo: groupNameById[String(student.hierarchy_two)] || "",
        hierarchyThree: groupNameById[String(student.hierarchy_three)] || "",
        termName: termNameById[String(filters.termId)] || "",
      };
      periodColumns.forEach((period) => {
        row[`period_${period.id}`] = resolveCellValue(period.id, student.username);
      });
      return row;
    });
  }, [filteredStudents, groupNameById, termNameById, filters.termId, periodColumns, resolveCellValue]);

  const handleSelectChange = (periodId, username, value) => {
    setStaged((prev) => ({
      ...prev,
      [`${periodId}::${username}`]: value,
    }));
  };

  const handleSave = () => {
    if (!canAccess) {
      handleToastCenterTop("저장 권한이 없습니다.");
      return;
    }
    const records = Object.entries(staged).map(([key, value]) => {
      const [period_id, username] = key.split("::");
      return { period_id, username, value };
    });
    if (records.length === 0) {
      handleToastCenterTop("저장할 변경사항이 없습니다.");
      return;
    }
    upsertAttendanceBulkMutation.mutate(
      {
        schoolid: schoolId,
        category: "parentEducation",
        records,
      },
      {
        onSuccess: () => {
          setStaged({});
          handleToastCenterTop("학부모교육 출결 저장 완료");
        },
        onError: () => {
          handleToastCenterTop("저장에 실패했습니다.");
        },
      },
    );
  };

  const showTable =
    Boolean(filters.schoolYearId) &&
    Boolean(filters.termId) &&
    Boolean(filters.hierarchyOneId) &&
    filters.hierarchyOneId !== "0";

  if (!showTable) {
    return (
      <div className="alert alert-info mt-2">
        학년도, 학기, 과정을 선택하면 조회/입력 표가 표시됩니다.
      </div>
    );
  }

  const tableConfig = {
    style: { fontSize: "13px", tableLayout: "auto" },
    headers: [
      { label: "과정", sortField: "hierarchyOne", style: { width: 70 } },
      { label: "학생", sortField: "fullname", style: { width: 90 } },
      { label: "학년", sortField: "hierarchyTwo", style: { width: 70 } },
      { label: "반", sortField: "hierarchyThree", style: { width: 70 } },
      { label: "학기", sortField: "termName", style: { width: 70 } },
      ...periodColumns.map((period) => ({
        label: (
          <span className={styles.headerTooltip} title={period.education_content || ""}>
            {period.session_name}
            {isIntegratedPeriod(period.hierarchy_one) ? "(통합)" : ""}
          </span>
        ),
        sortField: `period_${period.id}`,
        style: { minWidth: 88, width: 92 },
      })),
    ],
    columns: [
      { valueKey: "hierarchyOne" },
      { valueKey: "fullname" },
      { valueKey: "hierarchyTwo" },
      { valueKey: "hierarchyThree" },
      { valueKey: "termName" },
      ...periodColumns.map((period) => ({
        valueKey: `period_${period.id}`,
        valueFormatter: (val, row) => (
          <select
            className="form-select form-select-sm"
            value={val || ""}
            onChange={(e) => handleSelectChange(period.id, row.id, e.target.value)}
          >
            {ATTENDANCE_OPTIONS.map((option) => (
              <option key={option || "blank"} value={option}>
                {option}
              </option>
            ))}
          </select>
        ),
      })),
    ],
    rows: tableRows,
  };

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={upsertAttendanceBulkMutation.isLoading}
        >
          저장
        </button>
      </div>
      <DynamicTable
        tableConfig={tableConfig}
        dataStructure="flat"
        tableStyle="cleanModern"
        sortable={true}
        searchable={true}
        searchPlaceholder="학생 검색..."
        searchFields={["fullname", "hierarchyOne", "hierarchyTwo", "hierarchyThree"]}
        exportable={true}
        exportFileName={`학부모교육_조회입력_${filters.schoolYear || ""}_${termNameById[String(filters.termId)] || ""}`}
        exportButtonLabel="Excel"
        rowNumber={true}
      />
    </div>
  );
}
